import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ChangeDetectionStrategy,
  ViewEncapsulation
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FormControlItem } from '@app/shared/interfaces/form-control-item';
import { FormConfigItem } from '@app/shared/interfaces/form-config-item';
import { ConfigTypeField } from '@app/shared/types/config-type-field.type';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';
import { ModelAttributes } from '@app/core/interface/common';
import { FormHelper } from '@app/shared/helpers/form';
import { isEqual } from '@app/shared/helpers/is-equal';

/**
 * How wide a field needs to be for the value it actually holds.
 *
 * Forms used to size fields with a 12-column share written by hand at each
 * call site, which drifted into nonsense: a phone number — always fourteen
 * characters — took a whole row while a name that can be long got half of
 * one. Sizing from the field instead of from the grid means a new field is
 * correct by default and nobody has to pick a number.
 *
 * By name first (we know what a zip code is), then by type.
 */
const WIDTH_BY_NAME: Readonly<Record<string, number>> = {
  zip: 120,
  ssn: 170,
  phoneNumber: 190,
  dateOfBirth: 180,
  city: 190,
  state: 200,
  firstName: 200,
  lastName: 200,
  middleName: 160,
  relationship: 200,
  email: 260,
  address: 420,
};

const WIDTH_BY_TYPE: Readonly<Record<string, number>> = {
  number: 140,
  mask: 190,
  calendar: 190,
  dropdown: 210,
  string: 220,
};

/** Types whose control is a block of options or free text — always full row. */
const FULL_ROW_TYPES: ReadonlyArray<string> = ['textarea', 'radio', 'checkbox', 'switch'];

@Component({
  selector: 'app-form-constructor',
  templateUrl: './form-constructor.component.html',
  styleUrls: ['./form-constructor.component.sass'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormConstructorComponent implements OnDestroy {
  public form: FormGroup;

  public formControls: any;

  public onInputChange = new Subject();

  public dateNow = new Date();

  public currentYear = this.dateNow.getFullYear();

  public yearRange = `${this.currentYear - 100}:${this.currentYear}`;

  private _cachedValue;

  private _formConfig: FormConfigItem[] = [];

  /**
   * CSS width for one field. Returns a px value so the row wraps naturally
   * once it runs out of space, instead of stretching every input to fill a
   * column it does not need.
   */
  public fieldWidth(item: FormConfigItem): string {
    if (FULL_ROW_TYPES.indexOf(item.type as string) !== -1) {
      return '100%';
    }
    const byName = WIDTH_BY_NAME[item.name];
    if (byName) {
      return `${byName}px`;
    }
    // A mask states its own length: "+1 (999) 999 9999" needs more room than
    // "999-99-9999". Roughly 8.5px a character plus the input's padding.
    if (item.type === 'mask' && item.mask) {
      return `${Math.min(300, Math.max(150, Math.round(item.mask.length * 8.5) + 32))}px`;
    }
    return `${WIDTH_BY_TYPE[item.type as string] || 220}px`;
  }

  private _formChangeSubscription: Subscription;

  private _inputChangeSubscription: Subscription;

  private _subscription = new Subscription();

  /** Guards the deferred rebuild against a newer config racing in. */
  private _configSwap = 0;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  @Input() public set formConfig(val: FormConfigItem[]) {
    if (!isEqual(this._formConfig, val)) {
      this._cachedValue = null;
      this._formConfig = val;
      // Two-phase swap. Building the new FormGroup in the same pass that
      // tears down the old fields made destroyed controls (PrimeNG dropdowns
      // especially) resolve against the WRONG group — the "no FormControl
      // instance ... " / "reading 'dirty'" console errors on every tab
      // switch. Drop the form first so the old DOM unwinds against its own
      // group; the rebuild lands one microtask later.
      this.form = null;
      this.cdr.markForCheck();
      const swap = ++this._configSwap;
      Promise.resolve().then(() => {
        if (swap !== this._configSwap) {
          return; // a newer config arrived meanwhile
        }
        this.initForm();
        this.subscribeOnFormChange();
        this.subscribeOnInputChange();
        this.cdr.markForCheck();
      });
    }
  }

  public get formConfig(): FormConfigItem[] {
    return this._formConfig;
  }

  @Output() public changeFieldEvent = new EventEmitter<[value: ModelAttributes, message: string]>();

  @Output() public changeFormEvent = new EventEmitter<{valid: boolean, value: ModelAttributes}>();

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public isRequired(controlName: string): boolean {
    const control = this.form?.get(controlName);
    let result = false;

    if (control && control.validator) {
      const validator = control.validator({} as AbstractControl);
      result = validator && validator.required;
    }

    return result;
  }

  public isSwitch(controlName: string): boolean {
    return controlName === 'switch';
  }

  public isMask(controlName: string): boolean {
    return controlName === 'mask';
  }

  public isErrorIconVisible(controlName: string): boolean {
    const control = this.form?.controls[controlName];

    return !!control && control.dirty && control.invalid;
  }

  public isValidIconVisible(item: FormConfigItem): boolean {
    const control = this.form?.controls[item.name];

    return !!control &&
           item.type !== 'radio' &&
           item.type !== 'checkbox' &&
           control.valid &&
           control.value !== '' &&
           control.value !== undefined &&
           control.value !== null;
  }

  public getErrorMessage(key: string, control: FormControl | AbstractControl): string {
    return FormHelper.getErrorMessage(key, control);
  }

  /**
   * start the creation of the form according to the received config
   * @protected
   */
  protected initForm(): void {
    this.formControls = this.buildingFormControls(this.formConfig);
    this.form = new FormGroup(this.formControls);
  }

  protected subscribeOnFormChange(): void {
    if (this._formChangeSubscription) {
      this._subscription.remove(this._formChangeSubscription);
    }

    this._formChangeSubscription = this.form.statusChanges
      .subscribe(() => this.changeFormEvent.emit({ valid: this.form.valid, value: this.form.value }));

    this._subscription.add(this._formChangeSubscription);

    this.form.updateValueAndValidity();
  }

  protected subscribeOnInputChange(): void {
    if (this._inputChangeSubscription) {
      this._subscription.remove(this._inputChangeSubscription);
    }

    this._inputChangeSubscription = this.onInputChange
      .pipe(
        map(([event, item]): [value: ModelAttributes, label: string, isValid: boolean] => {
          const controlName = item.name;
          const controlType = item.type;
          const value = this.normalizeEventData(event, controlType, controlName);
          const isValid = this.form.controls[controlName].valid;

          return [
            { [controlName]: value },
            item.label || item.name,
            isValid
          ];
        }),
        debounceTime(1000),
        filter(([value, , isValid]) => {
          const equal = isEqual(this._cachedValue, value);
          if (isValid) {
            this._cachedValue = value;
          }

          return isValid && !equal;
        }),
      )
      .subscribe(([value, label]) => this.changeFieldEvent.next([value, label]));

    this._subscription.add(this._inputChangeSubscription);
  }

  /**
   * create FormControls based on the received form config
   * @param config
   * @protected
   */
  protected buildingFormControls(config: FormConfigItem[]): FormControlItem {
    return config.reduce(
      (accumulator, item): FormControlItem => {
        const validators = this.constructorValidators(item.validators);
        accumulator[item.name] = new FormControl({ value: item.value, disabled: item.disabled }, [...validators]);
        return accumulator;
      },
      {}
    );
  }

  /**
   * create validators for FormControl
   * @param validatorsList
   * @protected
   */
  protected constructorValidators(validatorsList = []): ValidatorFn[] {
    return validatorsList.reduce((accumulator, item) => {
      const {
        name,
        value,
      } = item;
      if (name && value) {
        accumulator.push(Validators[name](value));
      } else {
        accumulator.push(Validators[name]);
      }
      return accumulator;
    }, []);
  }

  /**
   * select the data to send to the server
   * @param event
   * @param controlType
   * @param controlName
   * @protected
   */
  protected normalizeEventData(event: any, controlType: ConfigTypeField, controlName: string): any {
    let value: any = null;
    switch (controlType) {
      case 'string':
      case 'textarea':
        value = event.target.value;
        break;

      case 'checkbox':
      case 'radio':
      case 'mask':
        value = this.form.value[controlName];
        break;

      case 'number':
        this.form.get(controlName).patchValue(event.value);
        value = this.form.value[controlName];
        break;

      case 'dropdown':
        value = event.value;
        break;

      case 'calendar':
        value = `${event.getTime()}`;
        break;

      case 'switch':
        value = event.checked;
    }

    return value;
  }
}
